// src/pages/Cloud/components/fsc/FSCEditClauseItemSidebar.js
import React, { useEffect, useMemo, useState } from 'react';
import { Offcanvas, Card, Button, Form } from 'react-bootstrap';
import { RadioGroup, FormControlLabel, Radio } from '@mui/material';

import toastAlert from '../../../../js/Base/ToastAlert';
import {
  buildNodeIndex,
  getSelectedPathPksFromLeaf,
  formatControlOptionLabel,
  statusLabel,
} from './fscEditClauseUtils';

const MAX_LEVELS = 4;

function safeTrim(v) {
  return String(v || '').trim();
}

/**
 * 第二層：選擇既有條款（逐層下拉）+ 編輯細項（同層）
 * - 只能選既有樹節點
 * - 選到 is_assessable === true 且非 read_only 才可儲存
 * - control_name 必填；status 可空（空=不變更當月狀態）
 */
export default function FSCEditClauseItemSidebar({
  show,
  onHide,
  tree,
  initialLeafPk,
  initialDraft,
  initialStatus, // 新增：'passed' | 'partial' | 'failed' | null
  onSave,
}) {
  const normalizedTree = useMemo(() => {
    const walk = (nodes) =>
      (nodes || []).map((n) => ({
        ...n,
        id: n.id ?? n.uuid,
        children: walk(n.children),
      }));

    return walk(tree || []);
  }, [tree]);

  const index = useMemo(() => buildNodeIndex(normalizedTree), [normalizedTree]);

  // 逐層下拉選擇
  const [selectedPks, setSelectedPks] = useState(() => Array.from({ length: MAX_LEVELS }, () => null));

  // 表單欄位
  const [controlName, setControlName] = useState('');
  const [status, setStatus] = useState(null);
  const [recommendation, setRecommendation] = useState('');
  const [remediationSteps, setRemediationSteps] = useState('');

  const currentLeaf = useMemo(() => {
    const lastPk = selectedPks.filter(Boolean).slice(-1)[0];
    if (!lastPk) return null;
    return index.byPk.get(lastPk)?.node || null;
  }, [selectedPks, index]);

  const currentPathNodes = useMemo(() => {
    const lastPk = selectedPks.filter(Boolean).slice(-1)[0];
    if (!lastPk) return [];
    return index.byPk.get(lastPk)?.path || [];
  }, [selectedPks, index]);

  const isEditableLeaf = Boolean(currentLeaf?.is_assessable && !currentLeaf?.read_only);

  // 每次開啟：若有 initialLeafPk 或 initialDraft，回填階層與欄位
  useEffect(() => {
    if (!show) return;

    // 回填階層
    if (initialLeafPk) {
      const pathPks = getSelectedPathPksFromLeaf(index, initialLeafPk);
      const next = Array.from({ length: MAX_LEVELS }, (_, idx) => pathPks[idx] || null);
      setSelectedPks(next);
    } else {
      setSelectedPks(Array.from({ length: MAX_LEVELS }, () => null));
    }
  }, [show, initialLeafPk, index]);

  // 當 leaf 決定後，回填表單（草稿優先，其次帶入當月預設狀態）
  useEffect(() => {
    if (!show) return;

    const shouldUseDraft = initialDraft && initialDraft?.leafPk && initialDraft.leafPk === initialLeafPk;

    if (shouldUseDraft) {
      setControlName(initialDraft?.edits?.control_name ?? '');
      setStatus(initialDraft?.edits?.status ?? null); // 草稿優先
      setRecommendation(initialDraft?.edits?.recommendation ?? '');
      setRemediationSteps(initialDraft?.edits?.remediation_steps ?? '');
      return;
    }

    if (!currentLeaf) {
      setControlName('');
      setStatus(null);
      setRecommendation('');
      setRemediationSteps('');
      return;
    }

    setControlName(currentLeaf?.control_name || '');
    setStatus(initialStatus ?? null); // 改成預設帶入
    setRecommendation(currentLeaf?.recommendation || '');
    setRemediationSteps(currentLeaf?.remediation_steps || '');
  }, [show, currentLeaf, initialDraft, initialLeafPk, initialStatus]);

  const handleClose = () => {
    onHide && onHide();
  };

  const handleSave = () => {
    const leafPk = selectedPks.filter(Boolean).slice(-1)[0];
    if (!leafPk) {
      toastAlert.autoClose(true).showError('請先選擇條款');
      return;
    }

    if (currentLeaf?.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法編輯');
      return;
    }

    if (!isEditableLeaf) {
      toastAlert.autoClose(true).showError('此節點不可編輯，請選擇最末層條款');
      return;
    }

    const name = safeTrim(controlName);
    if (!name) {
      toastAlert.autoClose(true).showError('條款名稱為必填');
      return;
    }

    onSave &&
      onSave({
        leafPk,
        edits: {
          control_name: name,
          status: status || null,
          recommendation: recommendation ?? '',
          remediation_steps: remediationSteps ?? '',
        },
      });
  };

  // 顯示路徑（disabled）與可編輯欄位：只有選到 leaf 才顯示
  const pathRows = useMemo(() => {
    const nodes = Array.isArray(currentPathNodes) ? currentPathNodes : [];
    return nodes.map((n, idx) => {
      const isLast = idx === nodes.length - 1;
      const title = isLast ? '條款名稱' : `階層${idx + 1}`;
      return { title, value: formatControlOptionLabel(n), isLast };
    });
  }, [currentPathNodes]);

  return (
    <Offcanvas show={show} onHide={handleClose} placement="end" backdrop="static" style={{ width: '50%' }}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title style={{ fontWeight: 'bold' }}>編輯條款細項</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
        <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
          <Card>
            <Card.Header style={{ fontWeight: 'bold' }}>條款</Card.Header>
            <Card.Body>
              {/* 逐層下拉（限制既有階層） */}
              {/* 選到可編輯 leaf 後才顯示編輯細項 */}
              {isEditableLeaf ? (
                <>
                  {/* 路徑顯示（disabled），最後一層允許改 name */}
                  {pathRows.map((r, idx) => {
                    if (!r.isLast) {
                      return (
                        <div key={idx} style={{ marginBottom: 12 }}>
                          <Form.Label style={{ fontWeight: 'bold' }}>{r.title}</Form.Label>
                          <Form.Control value={r.value} disabled />
                        </div>
                      );
                    }

                    return (
                      <div key={idx} style={{ marginBottom: 12 }}>
                        <Form.Label style={{ fontWeight: 'bold' }}>{r.title}</Form.Label>
                        <Form.Control value={controlName} onChange={(e) => setControlName(e.target.value)} />
                      </div>
                    );
                  })}

                  <div style={{ marginTop: 8, marginBottom: 12 }}>
                    <Form.Label style={{ fontWeight: 'bold' }}>條款是否通過</Form.Label>
                    <RadioGroup row value={status || ''} onChange={(e) => setStatus(e.target.value || null)}>
                      <FormControlLabel value="passed" control={<Radio size="small" />} label={statusLabel('passed')} />
                      <FormControlLabel
                        value="partial"
                        control={<Radio size="small" />}
                        label={statusLabel('partial')}
                      />
                      <FormControlLabel value="failed" control={<Radio size="small" />} label={statusLabel('failed')} />
                    </RadioGroup>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <Form.Label style={{ fontWeight: 'bold' }}>建議</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={recommendation}
                      onChange={(e) => setRecommendation(e.target.value)}
                    />
                  </div>

                  <div style={{ marginBottom: 0 }}>
                    <Form.Label style={{ fontWeight: 'bold' }}>補救步驟</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={remediationSteps}
                      onChange={(e) => setRemediationSteps(e.target.value)}
                    />
                  </div>
                </>
              ) : null}
            </Card.Body>
          </Card>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: '1px solid #e5e5e5',
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: '#fff',
          }}
        >
          <Button className="btn btn-cancel" variant="secondary" onClick={handleClose}>
            取消
          </Button>
          <Button className="btn btn-custom" variant="primary" onClick={handleSave}>
            儲存
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
